import classNames from "classnames";
import React from "react";
import defaultImage from "../images/default-product.jpeg";
import { DragAndDropIcon } from "@/icons/primary/drag-and-drop-icon";
import { currencyFormat } from "@/utils/numericHelpers";
import CaStatusDropdown, { CaStatusDropdownOption } from "../status-dropdown/CaStatusDropdown";
import CaButton from "../button/CaButton";
import { PencilIcon } from "@/icons/primary/pencil-icon";
import { ShareIcon } from "@/icons/primary/share-icon";
import { DuplicateIcon } from "@/icons/primary/duplicate-icon";
import { Upload2Icon } from "@/icons/primary-icons-new";

export type CaProductCardProps = {
  className?: string;
  style?: React.CSSProperties;
  name: string;
  price: number;
  status?: string | number | null;
  image?: string;
  onStatusChange?: (status?: string | number | null) => void;
  actions?: React.ReactNode;
  disabledByPaymentSetup?: boolean;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  draggableControlProps?: any;
  isSortable?: boolean;
  hidePrice?: boolean;
};

const STATUS_OPTIONS: CaStatusDropdownOption[] = [
  {
    label: "Public Content",
    value: "public",
    intent: "success",
  },
  // {
  //   label: "Members Only",
  //   value: "members_only",
  //   intent: "warning",
  // },
  {
    label: "Unlisted Content",
    value: "private",
    intent: "danger",
  },
];

function CaProductCard({
  className,
  style,
  name,
  price,
  status,
  image,
  onStatusChange,
  actions,
  disabledByPaymentSetup,
  onEdit,
  onDuplicate,
  onShare,
  onDownload,
  draggableControlProps,
  isSortable,
  hidePrice,
}: CaProductCardProps) {
  const imageSrc = image || defaultImage;

  const handleStatusChange = (option: CaStatusDropdownOption) => {
    onStatusChange && onStatusChange(option.value);
  };

  return (
    <section
      className={classNames(
        "relative flex justify-between items-center p-5 bg-white shadow-sm rounded-base gap-5",
        className,
      )}
      style={style}
    >
      {disabledByPaymentSetup && (
        <div
          className={classNames(
            "absolute top-0 left-0 w-full h-full bg-dark-neutral-4 opacity-50 rounded-base z-10",
          )}
        >
          <div className={classNames("flex justify-center items-center h-full")}>
            <span className={classNames("text-white text-lg font-sans-serif")}>
              Please complete your payment setup to start selling
            </span>
          </div>
        </div>
      )}
      <div className={classNames("flex justify-start items-center gap-5 w-full")}>
        {isSortable && (
          <div {...draggableControlProps} className='cursor-move'>
            <DragAndDropIcon className='!text-mid-neutral-3 text-lg' />
          </div>
        )}
        <div
          className={classNames(
            "flex justify-center items-center h-16 w-16 rounded-sm overflow-hidden",
          )}
        >
          <img
            className={classNames("h-16 w-16")}
            src={imageSrc}
            alt='product'
            style={{
              objectFit: "cover",
            }}
          />
        </div>
        <div>
          <div>
            <h4 className='text-base font-semibold font-sans-serif m-0'>{name}</h4>
            <div className={classNames("flex justify-start items-center gap-5")}>
              {!hidePrice && (
                <span
                  className={classNames("text-base font-sans-serif font-semibold text-orange m-0")}
                >
                  {price === 0 ? "Free" : currencyFormat(price)}
                </span>
              )}
              <CaStatusDropdown
                value={status}
                options={STATUS_OPTIONS}
                onChange={handleStatusChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classNames("flex justify-end items-center w-full text-right")}>
        {actions ? (
          actions
        ) : (
          <>
            {onDownload && (
              <CaButton size='small' minimal onClick={onDownload}>
                <Upload2Icon color='#5e678c' style={{ width: 23 }} />
              </CaButton>
            )}
            {onEdit && (
              <CaButton size='small' minimal onClick={onEdit}>
                <PencilIcon className='!text-mid-neutral-1 h-5 w-5 text-xl' />
              </CaButton>
            )}
            {onShare && (
              <CaButton size='small' minimal onClick={onShare}>
                <ShareIcon className='!text-mid-neutral-1 h-5 w-5 text-xl' />
              </CaButton>
            )}
            {onDuplicate && (
              <CaButton size='small' minimal onClick={onDuplicate}>
                <DuplicateIcon className='!text-mid-neutral-1 h-5 w-5 text-xl' />
              </CaButton>
            )}
          </>
        )}
      </div>
    </section>
  );
}

CaProductCard.defaultProps = {
  className: "",
  style: {},
  image: undefined,
  status: null,
  onStatusChange: () => {},
  actions: null,
  disabledByPaymentSetup: false,
  onEdit: undefined,
  onDuplicate: undefined,
  onShare: undefined,
  draggableControlProps: {},
  isSortable: true,
  hidePrice: false,
};

export default CaProductCard;
